/**
 * Canonical role groupings shared across the frontend.
 *
 * Centralised so a single string typo — e.g. exempting only `'Admin'` but not
 * `'Super Admin'` — can't silently diverge between the permission model and the
 * login location gate. That exact divergence once locked a Super Admin out of
 * the app entirely: their locations were cleared, and the sign-in gate (which
 * checked `role !== 'Admin'`) then refused to let them back in to fix it.
 */

import { userRoleSchema } from '@threadheads/shared';

/** Org-wide roles that are NOT scoped to specific locations. */
export const ADMIN_ROLES: ReadonlySet<string> = new Set(['Super Admin', 'Admin']);

/**
 * The film-only DTF Printer role. It keeps its sidebar and the bell (no
 * chrome-free station mode), and pages skip every procedure outside the
 * backend ROLE_PROCEDURE_ALLOWLIST for it (station settings, notification
 * settings, filing a redo from Film Storage).
 */
export const isDtfPrinterRole = (role: string | null | undefined): boolean =>
  role === userRoleSchema.enum['DTF Printer'];

/**
 * Production-station roles (scanner-first stations). 'DTF Printer' is a station
 * role here (it defaults to its first location and lands on Film Printing), but
 * on the backend it is NOT in STATION_ROLES: it reaches only the film tiers.
 */
export const STATION_ROLES: ReadonlySet<string> = new Set([
  'Picker',
  'Printer',
  'Catcher',
  'Packer',
  'DTF Printer',
]);

/**
 * True for Admin / Super Admin — the org-wide roles exempt from the login
 * location gate (they have access to every location by role, not by assignment).
 */
/**
 * Roles the `/production/issues` RoleGuard admits (App.tsx). Exported because the
 * Production Tracker's own allowlist is WIDER — it includes Picker/Printer/Catcher/
 * Packer — so any surface that LINKS from the tracker into Print Issues must gate on
 * THIS list rather than assume the viewer can follow it.
 *
 * A link a role's guard denies is not a harmless no-op: RoleGuard answers with a
 * silent `<Navigate replace />` to that role's station home, so the operator loses the
 * tracker, their expanded row and the order they were looking at, with no explanation.
 */
export const ISSUES_PAGE_ROLES: ReadonlySet<string> = new Set([
  'Super Admin', 'Admin', 'Warehouse Coordinator', 'Warehouse Staff', 'CX',
]);

export const canOpenIssuesPage = (role: string | null | undefined): boolean =>
  !!role && ISSUES_PAGE_ROLES.has(role);

/**
 * Roles the `/production/issues/resolve/:prodOrderId` RoleGuard admits (App.tsx).
 * Narrower than ISSUES_PAGE_ROLES by exactly ONE role: Warehouse Staff. They can
 * read the Issues surfaces but re-partitioning an order across locations is not
 * floor work. CX is admitted (BLU-842) because it is the role already talking to
 * the customer whose order is short, and making them wait for a coordinator to
 * press the button was the delay. The backend agrees: the same set of endpoints
 * sits on productionCoordinatorOrCxProcedure + the OrdersResolveLocations /
 * OrdersStatusOverride permissions.
 *
 * Exported so every entry point into the resolve board gates on the SAME list.
 * A "Split across locations" item shown to a role the guard denies is not a
 * harmless no-op: RoleGuard answers with a silent `<Navigate replace />` to that
 * role's station home, so the operator loses the list they were working and gets
 * no explanation. Hide the action instead.
 *
 * A readonly TUPLE rather than a Set (unlike ISSUES_PAGE_ROLES above): the
 * RoleGuard prop is typed `UserRole[]`, and spreading a `ReadonlySet<string>`
 * widens to `string[]`, which the guard rightly rejects. The literal tuple keeps
 * a typo here a compile error at the route.
 */
export const RESOLVE_BOARD_ROLES = ['Super Admin', 'Admin', 'Warehouse Coordinator', 'CX'] as const;

export const canOpenResolveBoard = (role: string | null | undefined): boolean =>
  !!role && (RESOLVE_BOARD_ROLES as readonly string[]).includes(role);

/**
 * Roles the `/production/settings` RoleGuard admits (App.tsx). Exported so a
 * link INTO a settings section (Base garments' "Open print size rules", its
 * DTF order tags link) gates on the same list as the route: a link the guard
 * denies is a silent `<Navigate replace />` to the role's home, not a no-op.
 * A readonly tuple for the same reason as RESOLVE_BOARD_ROLES.
 *
 * The role is half the answer: `ProductionAccessGuard` also sends an org that
 * runs no floor of its own to /orders. `useCanOpenProductionSettings` combines
 * both.
 */
export const PRODUCTION_SETTINGS_ROLES = ['Super Admin', 'Admin', 'Warehouse Coordinator'] as const;

export const canOpenProductionSettingsRole = (role: string | null | undefined): boolean =>
  !!role && (PRODUCTION_SETTINGS_ROLES as readonly string[]).includes(role);

/**
 * The backend `coordinatorProcedure` tier (apps/backend/src/trpc.ts): the
 * roles that may create a batch and re-run its buy list ("Update buy list").
 * A control a station role would only be refused on stays hidden from it.
 * KEEP IN LOCKSTEP with that procedure's requireRole list.
 */
export const COORDINATOR_ROLES = ['Super Admin', 'Admin', 'Warehouse Coordinator'] as const;

export const isCoordinatorRole = (role: string | null | undefined): boolean =>
  !!role && (COORDINATOR_ROLES as readonly string[]).includes(role);

export const isAdminRole = (role: string | null | undefined): boolean =>
  ADMIN_ROLES.has(role ?? '');

/**
 * Post-auth landing route for scanner-first station roles. Shared by
 * Login.tsx's post-sign-in redirect and RoleGuard.tsx's rejection-fallback so
 * the two can't drift the way ADMIN_ROLES once did (see file header).
 */
export const STATION_HOME_ROUTES: Readonly<Record<string, string>> = {
  Picker: '/production/picker',
  Printer: '/production/printer',
  Catcher: '/production/catcher',
  Packer: '/production/packer',
  'DTF Printer': '/production/film-printing',
};

/**
 * Where a DTF Printer with no usable floor of its own lands (BLU-1335): a plain
 * "no floor yet" screen, outside AppLayout and outside /production, so neither
 * ProductionAccessGuard nor the sidebar's hidesProduction redirect acts on it.
 */
export const NO_FLOOR_ROUTE = '/no-floor';

/**
 * Deny-by-default route allowlist for roles held to a few pages. A role with an
 * entry may open ONLY these paths; every other route redirects to its station
 * home (ProtectedRoute). Roles without an entry are unrestricted.
 *
 * Mirrors the backend ROLE_PROCEDURE_ALLOWLIST (apps/backend/src/lib/rbac.ts).
 * This is UX only: the server allowlist is the real fence, so a page added here
 * without its procedures still fails closed. '/notifications' is listed because
 * the bell's "See all" opens it (NotificationsBell).
 */
export const ROLE_ROUTE_ALLOWLIST: Readonly<Record<string, readonly string[]>> = {
  'DTF Printer': [
    '/production/film-printing',
    '/production/film-preprint',
    '/production/film-storage',
    '/help',
    '/notifications',
    NO_FLOOR_ROUTE,
  ],
};

/** Exact match after stripping one trailing slash; roles without an entry pass. */
export const isRouteAllowedForRole = (role: string | null | undefined, pathname: string): boolean => {
  if (!role) return true;
  const allowed = ROLE_ROUTE_ALLOWLIST[role];
  if (!allowed) return true;
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return allowed.includes(path);
};
